import React from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import useModal from '@tenant/utility/hooks/useModal';
import { LoadingButton } from '@mui/lab';
import { Formik } from 'formik';
import * as Yup from 'yup';
import DockerImageSelect from '@tenant/core/App/Select/DockerImageSelect';
import useAddAppComponent from 'redux/slices/appPackage/hooks/useAddAppComponent';

const validationSchema = Yup.object().shape({
  Name: Yup.string().required().max(255).label('Name'),
  Image: Yup.object().nullable().required().label('Image'),
});

const AddComponentDialog = ({
  appPackageId,
  callbackAfterSubmit = () => {},
}) => {
  const { visible, onShow, onClose } = useModal();
  const addMutate = useAddAppComponent();

  return (
    <>
      <Button variant='outlined' startIcon={<AddIcon />} onClick={onShow}>
        Add
      </Button>

      <Dialog open={visible} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle fontSize={16}>Add Component</DialogTitle>
        <Formik
          enableReinitialize
          validateOnChange
          validateOnBlur
          initialValues={{
            Name: '',
            Image: undefined,
          }}
          validationSchema={validationSchema}
          onSubmit={(data) => {
            addMutate.mutate(
              {
                appPackageId,
                components: [{ Name: data.Name, ImageName: data.Image?.Name }],
              },
              {
                onSuccess(data) {
                  callbackAfterSubmit(data);
                  onClose();
                },
              },
            );
          }}
        >
          {({ submitForm, values, touched, setFieldValue, errors }) => (
            <>
              <DialogContent>
                <Grid container spacing={5}>
                  <Grid item xs={12} sm={12}>
                    <Box
                      component='p'
                      color='text.primary'
                      fontSize={14}
                      mb={2}
                    >
                      Name
                      <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                        *
                      </Box>
                    </Box>
                    <TextField
                      fullWidth
                      value={values.Name ?? ''}
                      error={touched.Name && !!errors.Name}
                      helperText={touched.Name ? errors.Name : ''}
                      placeholder='E.g. Joblogic app component'
                      onChange={(e) =>
                        setFieldValue('Name', e.target.value, true)
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={12}>
                    <Box
                      component='p'
                      color='text.primary'
                      fontSize={14}
                      mb={2}
                    >
                      Image
                      <Box component='span' ml={1} sx={{ color: 'error.main' }}>
                        *
                      </Box>
                    </Box>
                    <DockerImageSelect
                      fullWidth
                      isMultiple={false}
                      value={values.Image || null}
                      error={touched.Image && !!errors.Image}
                      helperText={touched.Image ? errors.Image : ''}
                      onChange={(e, value) => {
                        setFieldValue('Image', value);
                      }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={onClose}
                  disabled={addMutate.isPending}
                >
                  Cancel
                </Button>
                <LoadingButton
                  variant='contained'
                  loading={addMutate.isPending}
                  onClick={submitForm}
                >
                  Save
                </LoadingButton>
              </DialogActions>
            </>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default React.memo(AddComponentDialog);

AddComponentDialog.propTypes = {
  appPackageId: PropTypes.string,
};
